import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, LineSeries, IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import { fetchStockHistory } from '../Strategy/AssetComparison';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatMarketPrice } from '../../utils/formatters';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Line-only price chart for stocks / ETFs on the detail page. Separate from the
 * crypto TradingChart (which is CoinGecko + candles); this uses the Yahoo history
 * proxy via the shared fetchStockHistory (daily closes, converted to USD). The
 * proxy provides no OHLC/volume/intraday, so this is line-only with 1M/3M/1Y.
 *
 * Plotted in the display currency at TODAY's rate, not each day's. The history
 * arrives as native price x today's USD rate (see fetchStockHistory), so
 * multiplying by today's display rate gets back to the native price exactly when
 * the display currency is the listing currency: an ASX ETF shown in AUD plots its
 * real AUD closes. Per-day rates would bend that line by the USD rate's movement.
 */

export type StockRange = '1M' | '3M' | '1Y';

interface Props {
  stockId: string;
  range: StockRange;
}

const LINE_COLOR = '#d4b483';

function makeChartOpts(isDark: boolean) {
  return {
    layout: {
      background: { color: isDark ? '#161616' : '#ffffff' },
      textColor:  isDark ? '#888888' : '#555555',
      fontSize:   11,
    },
    grid: {
      vertLines: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
      horzLines: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
    },
    rightPriceScale: { borderVisible: false },
    timeScale:       { borderVisible: false },
  };
}

export function StockChart({ stockId, range }: Props) {
  const { theme } = useTheme();
  const isDark    = theme === 'dark';
  const { currency, rate } = useCurrency();

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);
  const lineRef      = useRef<ISeriesApi<'Line'> | null>(null);

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // ── Mount: create chart + line series once ──────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const initDark = document.documentElement.classList.contains('dark');
    const chart = createChart(containerRef.current, {
      ...makeChartOpts(initDark),
      width:  containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });
    const line = chart.addSeries(LineSeries, {
      color: LINE_COLOR,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.applyOptions({
        width:  containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    });
    ro.observe(containerRef.current);

    chartRef.current = chart;
    lineRef.current  = line;

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      lineRef.current  = null;
    };
  }, []);

  // ── Apply theme on change ───────────────────────────────────────────────────
  useEffect(() => {
    chartRef.current?.applyOptions(makeChartOpts(isDark));
  }, [isDark]);

  // ── Price scale and last-value label in the display currency ───────────────
  useEffect(() => {
    chartRef.current?.applyOptions({
      localization: { priceFormatter: (p: number) => formatMarketPrice(p, currency.code) },
    });
  }, [currency.code]);

  // fetchStockHistory returns { ts (ms), price (USD) }. Lightweight-charts wants
  // unique, ascending seconds, so dedupe by day-second. Kept in USD here so a
  // currency change re-plots without refetching.
  const [usdData, setUsdData] = useState<{ time: UTCTimestamp; value: number }[] | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!lineRef.current) return;
    setLoading(true);
    setError(null);
    try {
      const pts = await fetchStockHistory(stockId, range);
      if (!lineRef.current) return;
      const dedup = new Map<number, number>();
      for (const p of pts) dedup.set(Math.floor(p.ts / 1000), p.price);
      setUsdData(
        Array.from(dedup.entries())
          .sort(([a], [b]) => a - b)
          .map(([time, value]) => ({ time: time as UTCTimestamp, value }))
      );
    } catch {
      setError('Could not load price history for this asset. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [stockId, range, retryCount]);

  useEffect(() => { load(); }, [load]);

  // ── Load into the series, converted ─────────────────────────────────────────
  const fittedForRef = useRef<typeof usdData>(null);
  useEffect(() => {
    if (!usdData || !lineRef.current) return;
    lineRef.current.setData(usdData.map((d) => ({ time: d.time, value: d.value * rate })));
    // Fit only for new data, not when only the currency changed.
    if (fittedForRef.current !== usdData) {
      chartRef.current?.timeScale().fitContent();
      fittedForRef.current = usdData;
    }
  }, [usdData, rate]);

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ visibility: (loading || error) ? 'hidden' : 'visible' }}
      />

      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
          style={{ background: isDark ? '#161616' : '#ffffff' }}>
          <Loader2 className="w-7 h-7 text-slate-400 animate-spin" />
          <span className="text-sm text-slate-500">Loading chart…</span>
        </div>
      )}

      {!loading && error && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center"
          style={{ background: isDark ? '#161616' : '#ffffff' }}
        >
          <AlertCircle className="w-7 h-7 text-red-400" />
          <p className="text-sm text-slate-400 max-w-xs">{error}</p>
          <button
            onClick={() => setRetryCount((c) => c + 1)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100 bg-white/[0.06] hover:bg-white/[0.1] border border-slate-700 px-4 py-2 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
